// Academic Planning Service - Manages academic years and events

import { apiGet, apiPost, apiPatch, apiDelete, API_BASE, getAuthHeaders } from '@/utils/api';

// Enum types
export enum AcademicEventType {
  PARASCOLAIRE = 'PARASCOLAIRE',
  SPECIAL = 'SPECIAL',
  HOLIDAY = 'HOLIDAY',
  EXAM_PERIOD = 'EXAM_PERIOD',
  VACATION = 'VACATION',
  RENTREE = 'RENTREE',
  OTHER = 'OTHER',
}

export const ACADEMIC_EVENT_TYPE_LABELS: Record<AcademicEventType, string> = {
  [AcademicEventType.PARASCOLAIRE]: 'Evenement parascolaire',
  [AcademicEventType.SPECIAL]: 'Evenement special',
  [AcademicEventType.HOLIDAY]: 'Jour ferie',
  [AcademicEventType.EXAM_PERIOD]: 'Periode d\'examens',
  [AcademicEventType.VACATION]: 'Vacances',
  [AcademicEventType.RENTREE]: 'Rentree',
  [AcademicEventType.OTHER]: 'Autre',
};

export const ACADEMIC_EVENT_TYPE_COLORS: Record<AcademicEventType, string> = {
  [AcademicEventType.PARASCOLAIRE]: '#10B981', // green
  [AcademicEventType.SPECIAL]: '#8B5CF6', // purple
  [AcademicEventType.HOLIDAY]: '#EF4444', // red
  [AcademicEventType.EXAM_PERIOD]: '#F59E0B', // amber
  [AcademicEventType.VACATION]: '#3B82F6', // blue
  [AcademicEventType.RENTREE]: '#06B6D4', // cyan
  [AcademicEventType.OTHER]: '#6B7280', // gray
};

// Interfaces
export interface AcademicYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isCurrent: boolean;
  tenantId: string;
  semester1StartDate?: string;
  semester1EndDate?: string;
  semester2StartDate?: string;
  semester2EndDate?: string;
  workingHoursStart?: string;
  workingHoursEnd?: string;
  rattrapageStartDate?: string;
  rattrapageEndDate?: string;
  deliberationSemester1?: string;
  deliberationSemester2?: string;
  deliberationRattrapage?: string;
  createdAt: string;
  updatedAt: string;
  events?: AcademicEvent[];
  _count?: {
    events: number;
    students: number;
    staffUsers: number;
  };
}

export interface AcademicEvent {
  id: string;
  academicYearId: string;
  title: string;
  description?: string;
  eventType: AcademicEventType;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  color?: string;
  isRecurring: boolean;
  createdAt: string;
  updatedAt: string;
  academicYear?: {
    id: string;
    name: string;
  };
}

export interface CreateAcademicYearDto {
  name: string;
  startDate: string;
  endDate: string;
  isCurrent?: boolean;
  semester1StartDate?: string;
  semester1EndDate?: string;
  semester2StartDate?: string;
  semester2EndDate?: string;
  workingHoursStart?: string;
  workingHoursEnd?: string;
}

export interface UpdateAcademicYearDto extends Partial<CreateAcademicYearDto> {
  isActive?: boolean;
}

export interface CreateAcademicEventDto {
  academicYearId: string;
  title: string;
  description?: string;
  eventType: AcademicEventType;
  startDate: string;
  endDate: string;
  isAllDay?: boolean;
  color?: string;
  isRecurring?: boolean;
}

export interface UpdateAcademicEventDto {
  title?: string;
  description?: string;
  eventType?: AcademicEventType;
  startDate?: string;
  endDate?: string;
  isAllDay?: boolean;
  color?: string;
  isRecurring?: boolean;
}

// ==================== ACADEMIC YEARS ====================

// Legacy function with token parameter - uses raw fetch for backward compatibility
// NOTE: the `token` parameter is IGNORED and kept only for backward
// compatibility with the ~18 existing callers. Passing the auth token from the
// Zustand store was buggy: the API layer silently refreshes the access token in
// localStorage on 401, but the store copy goes stale, so this request would
// 401 and callers (which .catch(() => [])) ended up with empty dropdowns.
// Delegating to apiGet reads a FRESH token + tenantId from localStorage and
// auto-refreshes on 401 — fixing every caller at once, for all tenants.
export async function fetchAcademicYears(_token?: string): Promise<AcademicYear[]> {
  return getAcademicYears();
}

// Legacy function with token parameter
// `token` is IGNORED (kept for backward compatibility). Same stale-store-token
// bug as fetchAcademicYears — delegate to the apiGet path so every caller reads
// a fresh token + tenantId and auto-refreshes on 401.
export async function fetchCurrentAcademicYear(_token?: string): Promise<AcademicYear | null> {
  return getCurrentAcademicYear();
}

/**
 * Get current academic year using auth from store (no token parameter needed)
 * The backend auto-creates the current year if it doesn't exist
 */
export async function getCurrentAcademicYear(): Promise<AcademicYear | null> {
  const response = await fetch(`${API_BASE}/academic-planning/years/current`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json().catch(() => ({ message: 'Erreur de recuperation de l\'annee academique actuelle' }));
    throw new Error(error.message || 'Erreur de recuperation de l\'annee academique actuelle');
  }

  // Handle empty response body (when no academic year exists)
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Legacy function with token parameter
export async function fetchAcademicYearById(token: string, id: string): Promise<AcademicYear> {
  const response = await fetch(`${API_BASE}/academic-planning/years/${id}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de recuperation de l\'annee academique' }));
    throw new Error(error.message || 'Erreur de recuperation de l\'annee academique');
  }

  return response.json();
}

// Legacy function with token parameter
export async function createAcademicYear(token: string, data: CreateAcademicYearDto): Promise<AcademicYear> {
  const response = await fetch(`${API_BASE}/academic-planning/years`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de creation de l\'annee academique' }));
    throw new Error(error.message || 'Erreur de creation de l\'annee academique');
  }

  return response.json();
}

// Legacy function with token parameter
export async function updateAcademicYear(token: string, id: string, data: UpdateAcademicYearDto): Promise<AcademicYear> {
  const response = await fetch(`${API_BASE}/academic-planning/years/${id}`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de mise a jour de l\'annee academique' }));
    throw new Error(error.message || 'Erreur de mise a jour de l\'annee academique');
  }

  return response.json();
}

// Legacy function with token parameter
export async function deleteAcademicYear(token: string, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/academic-planning/years/${id}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de suppression de l\'annee academique' }));
    throw new Error(error.message || 'Erreur de suppression de l\'annee academique');
  }
}

// Legacy function with token parameter
export async function setCurrentAcademicYear(token: string, id: string): Promise<AcademicYear> {
  const response = await fetch(`${API_BASE}/academic-planning/years/${id}/set-current`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de definition de l\'annee academique actuelle' }));
    throw new Error(error.message || 'Erreur de definition de l\'annee academique actuelle');
  }

  return response.json();
}

// ==================== ACADEMIC EVENTS ====================

// Legacy function with token parameter
export async function fetchAcademicEvents(token: string, academicYearId?: string): Promise<AcademicEvent[]> {
  const url = academicYearId
    ? `${API_BASE}/academic-planning/events?academicYearId=${academicYearId}`
    : `${API_BASE}/academic-planning/events`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de recuperation des evenements' }));
    throw new Error(error.message || 'Erreur de recuperation des evenements');
  }

  return response.json();
}

// Legacy function with token parameter
export async function fetchAcademicEventById(token: string, id: string): Promise<AcademicEvent> {
  const response = await fetch(`${API_BASE}/academic-planning/events/${id}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de recuperation de l\'evenement' }));
    throw new Error(error.message || 'Erreur de recuperation de l\'evenement');
  }

  return response.json();
}

// Legacy function with token parameter
export async function createAcademicEvent(token: string, data: CreateAcademicEventDto): Promise<AcademicEvent> {
  const response = await fetch(`${API_BASE}/academic-planning/events`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de creation de l\'evenement' }));
    throw new Error(error.message || 'Erreur de creation de l\'evenement');
  }

  return response.json();
}

// Legacy function with token parameter
export async function updateAcademicEvent(token: string, id: string, data: UpdateAcademicEventDto): Promise<AcademicEvent> {
  const response = await fetch(`${API_BASE}/academic-planning/events/${id}`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de mise a jour de l\'evenement' }));
    throw new Error(error.message || 'Erreur de mise a jour de l\'evenement');
  }

  return response.json();
}

// Legacy function with token parameter
export async function deleteAcademicEvent(token: string, id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/academic-planning/events/${id}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur de suppression de l\'evenement' }));
    throw new Error(error.message || 'Erreur de suppression de l\'evenement');
  }
}

// ==================== USER/STUDENT ASSIGNMENT ====================

// Legacy function with token parameter
export async function assignUserToAcademicYear(token: string, userId: string, academicYearId: string | null): Promise<void> {
  const response = await fetch(`${API_BASE}/academic-planning/users/${userId}/assign-year`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ academicYearId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur d\'assignation de l\'utilisateur' }));
    throw new Error(error.message || 'Erreur d\'assignation de l\'utilisateur');
  }
}

// Legacy function with token parameter
export async function assignStudentToAcademicYear(token: string, studentId: string, academicYearId: string | null): Promise<void> {
  const response = await fetch(`${API_BASE}/academic-planning/students/${studentId}/assign-year`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ academicYearId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur d\'assignation de l\'etudiant' }));
    throw new Error(error.message || 'Erreur d\'assignation de l\'etudiant');
  }
}

// Legacy function with token parameter
export async function bulkAssignUsersToAcademicYear(token: string, userIds: string[], academicYearId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/academic-planning/bulk-assign/users`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ userIds, academicYearId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur d\'assignation en masse des utilisateurs' }));
    throw new Error(error.message || 'Erreur d\'assignation en masse des utilisateurs');
  }
}

// Legacy function with token parameter
export async function bulkAssignStudentsToAcademicYear(token: string, studentIds: string[], academicYearId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/academic-planning/bulk-assign/students`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ studentIds, academicYearId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Erreur d\'assignation en masse des etudiants' }));
    throw new Error(error.message || 'Erreur d\'assignation en masse des etudiants');
  }
}

// ==================== NEW API-BASED FUNCTIONS ====================
// These use the centralized api utilities and don't require token parameter

export async function getAcademicYears(): Promise<AcademicYear[]> {
  return apiGet<AcademicYear[]>('/academic-planning/years');
}

export async function getAcademicYearById(id: string): Promise<AcademicYear> {
  return apiGet<AcademicYear>(`/academic-planning/years/${id}`);
}

export async function createAcademicYearNew(data: CreateAcademicYearDto): Promise<AcademicYear> {
  return apiPost<AcademicYear>('/academic-planning/years', data);
}

export async function updateAcademicYearNew(id: string, data: UpdateAcademicYearDto): Promise<AcademicYear> {
  return apiPatch<AcademicYear>(`/academic-planning/years/${id}`, data);
}

export async function deleteAcademicYearNew(id: string): Promise<void> {
  return apiDelete<void>(`/academic-planning/years/${id}`);
}

export async function setCurrentAcademicYearNew(id: string): Promise<AcademicYear> {
  return apiPatch<AcademicYear>(`/academic-planning/years/${id}/set-current`);
}

export async function getAcademicEvents(academicYearId?: string): Promise<AcademicEvent[]> {
  const endpoint = academicYearId
    ? `/academic-planning/events?academicYearId=${academicYearId}`
    : '/academic-planning/events';
  return apiGet<AcademicEvent[]>(endpoint);
}

export async function getAcademicEventById(id: string): Promise<AcademicEvent> {
  return apiGet<AcademicEvent>(`/academic-planning/events/${id}`);
}

export async function createAcademicEventNew(data: CreateAcademicEventDto): Promise<AcademicEvent> {
  return apiPost<AcademicEvent>('/academic-planning/events', data);
}

export async function updateAcademicEventNew(id: string, data: UpdateAcademicEventDto): Promise<AcademicEvent> {
  return apiPatch<AcademicEvent>(`/academic-planning/events/${id}`, data);
}

export async function deleteAcademicEventNew(id: string): Promise<void> {
  return apiDelete<void>(`/academic-planning/events/${id}`);
}

export async function assignUserToAcademicYearNew(userId: string, academicYearId: string | null): Promise<void> {
  return apiPatch<void>(`/academic-planning/users/${userId}/assign-year`, { academicYearId });
}

export async function assignStudentToAcademicYearNew(studentId: string, academicYearId: string | null): Promise<void> {
  return apiPatch<void>(`/academic-planning/students/${studentId}/assign-year`, { academicYearId });
}

export async function bulkAssignUsersToAcademicYearNew(userIds: string[], academicYearId: string): Promise<void> {
  return apiPatch<void>('/academic-planning/bulk-assign/users', { userIds, academicYearId });
}

export async function bulkAssignStudentsToAcademicYearNew(studentIds: string[], academicYearId: string): Promise<void> {
  return apiPatch<void>('/academic-planning/bulk-assign/students', { studentIds, academicYearId });
}

// Helper function to format date for display
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// Helper function to format date for input
export function formatDateForInput(dateString: string): string {
  return dateString.split('T')[0];
}

/**
 * Generate academic year name in format YYYY/YYYY+1
 * @param year The starting year (e.g., 2024 for 2024/2025)
 */
export function generateAcademicYearName(year: number): string {
  return `${year}/${year + 1}`;
}

/**
 * Get the current academic year based on the current date
 * Academic year starts in September
 * @returns The starting year of the current academic year
 */
export function getCurrentAcademicStartYear(): number {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11

  // If we're before September, we're still in the previous academic year
  // September = month 8 (0-indexed)
  if (currentMonth < 8) {
    return currentYear - 1;
  }
  return currentYear;
}

/**
 * Generate a list of academic year options for a dropdown
 * Includes previous years and future years around the current academic year
 * @param yearsBefore Number of years before current academic year to include (default 3)
 * @param yearsAfter Number of years after current academic year to include (default 2)
 */
export function generateAcademicYearOptions(yearsBefore: number = 3, yearsAfter: number = 2): { value: string; label: string }[] {
  const currentStartYear = getCurrentAcademicStartYear();
  const options: { value: string; label: string }[] = [];

  for (let year = currentStartYear - yearsBefore; year <= currentStartYear + yearsAfter; year++) {
    const yearName = generateAcademicYearName(year);
    options.push({
      value: yearName,
      label: yearName,
    });
  }

  return options;
}

/**
 * Get the default academic year name (current academic year)
 */
export function getDefaultAcademicYearName(): string {
  return generateAcademicYearName(getCurrentAcademicStartYear());
}
